import { memo, useCallback, useEffect, useRef } from 'react';
import { DataTreeNodes } from 'shared/data_tree/DataTreeNodes';
import {
    Tree,
    TreeExpansionConfig,
    TreeMovableConfig,
    TreeNode,
    TreeNodeId,
    TreeNodeRenderer,
    TreeSelectionConfig,
} from 'shared/data_tree/types';
import { KeyboardEventCode } from 'utils/key_codes';

function isArrowKey(key: KeyboardEventCode): boolean {
    return (
        key === KeyboardEventCode.ArrowUp ||
        key === KeyboardEventCode.ArrowDown ||
        key === KeyboardEventCode.ArrowRight ||
        key === KeyboardEventCode.ArrowLeft
    );
}

export type DataTreeProps<TData> = Readonly<{
    /**
     * Unique identifier for the tree.
     */
    treeId: string;
    /**
     * Data for the the tree.
     */
    data: Tree<TData>;
    /**
     * Callback to render a tree node.
     */
    renderNode: TreeNodeRenderer<TData>;
    /**
     * Node expansion configuration.
     */
    expandable?: TreeExpansionConfig<TData>;
    /**
     * Node selection configuration.
     */
    selectable?: TreeSelectionConfig<TData>;
    /**
     * Node drag and drop configuration.
     */
    movable?: TreeMovableConfig<TData>;
    /**
     * Node to scroll into view if not visible. Waits until `isTreeLoading` is false to scroll into view.
     */
    scrollToNodeId?: TreeNodeId;
    /**
     * Whether anything in the tree is loading. Defaults to `false`.
     */
    isTreeLoading?: boolean;
    /**
     * Callback function called when tree node elements are mounted/unmounted.
     * Called with the node ID and element reference (null when unmounting).
     */
    onNodeRef?: (nodeId: TreeNodeId, element: HTMLElement | null) => void;
}>;

/**
 * Renders a tree.
 */
function DataTree<TData>({
    treeId,
    data,
    renderNode,
    expandable,
    selectable,
    movable,
    scrollToNodeId,
    isTreeLoading,
}: DataTreeProps<TData>) {
    const nodeElementById = useRef(new Map<string, HTMLElement>());

    useEffect(() => {
        const handler = (e: KeyboardEvent): void => {
            const el = document.activeElement;
            if (el && el.classList.contains('tree-node-btn')) {
                if (isArrowKey(e.code as KeyboardEventCode)) {
                    e.preventDefault();
                }
                const node = data.get(el.id);
                function select(node: TreeNode<TData>) {
                    if (selectable?.canSelect?.(node)) {
                        nodeElementById.current.get(node.id)?.focus();
                    }
                }
                const next = ((): TreeNode<TData> | undefined => {
                    switch (e.code as KeyboardEventCode) {
                        case KeyboardEventCode.ArrowUp:
                        case KeyboardEventCode.KeyK:
                            if (node.prevSibling === undefined) {
                                return node.parent;
                            }
                            if (
                                expandable?.isExpanded(node.prevSibling) &&
                                node.prevSibling.children?.[0]
                            ) {
                                return node.prevSibling.children[
                                    node.prevSibling.children.length - 1
                                ];
                            }
                            return node.prevSibling;
                        case KeyboardEventCode.ArrowDown:
                        case KeyboardEventCode.KeyJ:
                            if (expandable?.isExpanded(node) && node.children?.[0]) {
                                return node.children[0];
                            }
                            if (node.nextSibling === undefined) {
                                let n: TreeNode<TData> | undefined = node;
                                while ((n = n.parent)) {
                                    const ns = n.nextSibling;
                                    if (ns) {
                                        return ns;
                                    }
                                }
                            }
                            return node.nextSibling;
                        case KeyboardEventCode.ArrowRight:
                        case KeyboardEventCode.KeyL: {
                            if (
                                expandable &&
                                !expandable.isExpanded(node) &&
                                expandable.canExpand?.(node)
                            ) {
                                expandable.onChange({
                                    event: e,
                                    isExpanded: false,
                                    node,
                                });
                                return;
                            }
                            return node.children?.[0];
                        }
                        case KeyboardEventCode.ArrowLeft:
                        case KeyboardEventCode.KeyH:
                            if (expandable?.isExpanded(node)) {
                                expandable.onChange({
                                    event: e,
                                    isExpanded: expandable.isExpanded(node),
                                    node,
                                });
                                return;
                            }
                            return node.parent;
                    }
                })();
                if (next === undefined) {
                    return;
                }
                select(next);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [data, expandable, selectable]);

    const onNodeRefCallback = useCallback(
        (nodeId: TreeNodeId, element: HTMLElement | null) => {
            if (element) {
                nodeElementById.current.set(nodeId, element);
            } else {
                nodeElementById.current.delete(nodeId);
            }
        },
        []
    );

    return (
        <DataTreeNodes<TData>
            treeId={treeId}
            nodes={data.rootNodes}
            renderNode={renderNode}
            expandable={expandable}
            selectable={selectable}
            movable={movable}
            scrollToNodeId={scrollToNodeId}
            isTreeLoading={isTreeLoading}
            onNodeRef={onNodeRefCallback}
        />
    );
}

const memoDataTree = memo(DataTree) as typeof DataTree;
export { memoDataTree as DataTree };
